import { mutate } from 'swrv'
import axios from '@/lib/API/client'
import { i18n, supportedLocales } from '@/i18n'
import { readHttpCache, removeHttpCache, writeHttpCache } from '@/lib/sqlite/features/http-cache-feature'
import { isLikelyNetworkError } from './network-error'
import { createFetcher } from './fetcher-core'
import { cacheRefusal } from './swrv-refusal'

// API payloads are server-localized (X-localization header), so the offline
// cache identity must include the locale: a response cached in one language
// must never be served while the app runs in another.
function storageKey(key, locale = i18n.global.locale.value || 'en-US') {
    return `${locale}|${key}`
}

async function readCache(key) {
    try {
        return await readHttpCache(storageKey(key))
    } catch {
        return null
    }
}

async function writeCache(key, data) {
    try {
        await writeHttpCache(storageKey(key), data)
    } catch (error) {
        console.warn('[Fetcher] Cache write failed', key, error)
    }
}

// Only a refusal removes a copy, and it removes it in every language: each one
// is still the content the reader may no longer see.
async function removeCache(key) {
    const storageKeys = new Set([storageKey(key), ...supportedLocales.map(({ locale }) => storageKey(key, locale))])
    for (const keyInLocale of storageKeys) {
        try {
            await removeHttpCache(keyInLocale)
        } catch (error) {
            console.warn('[Fetcher] Cache removal failed', keyInLocale, error)
        }
    }
}

const fetcher = createFetcher({
    get: (key) => axios.get(key),
    readCache,
    writeCache,
    removeCache,
    refuse: cacheRefusal,
    mutate: (key, value) => mutate(key, value),
    isOffline: () => typeof navigator !== 'undefined' && !navigator.onLine,
    isNetworkError: isLikelyNetworkError,
    warn: (...args) => console.warn(...args),
})

export default fetcher

// From the network only, never from nor into the offline copy (null offline): for what
// the reader acts on, where a stale row would offer to answer what is already answered.
export const fetchFresh = (key) => fetcher(key, undefined, { disableCache: true })

// Reads `key` again, fresh unless told how, and hands the answer to everything showing it.
export const reloadFresh = (key, read = fetchFresh) => mutate(key, read(key))
